/* eslint-disable camelcase */
import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import {
  setSelectedAgentSiteCode,
  setSelectedAgentName,
  setSelectedAgentMode,
} from '../../slices/AppsSlice';

import LoadingSpinner from '../../components/LoadingSpinner';
import AgentCard from '../../components/AgentCard';
import { usePageMeta } from '../../contexts/PageMetaContext';
import useApiCall from '../../hooks/useApiCall';
import useToast from '../../hooks/useToast';
import { TOAST } from '../../constants/ToastMessages';
import { SLATE, INDIGO } from '../../constants/Colors';
import { FILES_CLOUDFRONT_BASE_URL, PLACEHOLDER_LOGO_URL_S3 } from '../../constants/Constants';

// ─── Component ────────────────────────────────────────────────────────────────
export default function ManageAgents() {
  const currentApp = useSelector(state => state?.apps?.selectedApp);
  const tokens = useSelector(state => state?.tokens.value);
  const { makeRequest } = useApiCall();
  const { success, error: showError } = useToast();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { setPageMeta } = usePageMeta();

  const { bu_id } = useParams();
  const [searchParams] = useSearchParams();
  const businessUnitId = bu_id || searchParams.get('business_unit_id');

  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buName, setBuName] = useState(null);
  const [exportConfirmOpen, setExportConfirmOpen] = useState(false);
  const exportInFlight = useRef(false);

  const exportApp = async () => {
    if (!currentApp?.id || exportInFlight.current) return;
    exportInFlight.current = true;
    try {
      await makeRequest(`/v1/apps/${currentApp.id}/exports/export`, {
        method: 'POST',
        authTokens: tokens,
        ignore_error: true,
      });
      success(TOAST.app.exported);
    } catch {
      showError(TOAST.app.exportError);
    } finally {
      exportInFlight.current = false;
    }
  };

  const confirmExportApp = () => {
    setExportConfirmOpen(false);
    exportApp();
  };

  // Fetch BU name when arriving via the /business-units/:bu_id/agents route
  useEffect(() => {
    if (!bu_id || !tokens || !currentApp?.id) return;
    makeRequest(`/v1/apps/${currentApp?.id}/business_units/${bu_id}`, {
      method: 'GET',
      authTokens: tokens,
    })
      .then(data => {
        const name = data?.business_unit?.name;
        if (name) setBuName(name);
      })
      .catch(() => {});
  }, [bu_id, currentApp?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setPageMeta(prev => ({
      ...prev,
      itemName: buName || undefined,
    }));
  }, [buName, setPageMeta]);

  useEffect(() => {
    const createPath =
      currentApp?.client_mode === 'legacy'
        ? `/${currentApp?.url_code}/agents/new?mode=legacy`
        : `/${currentApp?.url_code}/agents/select-type`;
    setPageMeta({
      description: `Manage AI agents for ${currentApp?.client_name || 'this client'}`,
      actions: (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            disabled={!currentApp?.id}
            onClick={() => setExportConfirmOpen(true)}
            sx={{
              textTransform: 'none',
              fontSize: '0.875rem',
              fontWeight: 500,
            }}
          >
            Export Client
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon sx={{ fontSize: '1rem' }} />}
            onClick={() => navigate(createPath)}
            sx={{
              textTransform: 'none',
              fontSize: '0.875rem',
              fontWeight: 500,
              boxShadow: 'none',
              '&:hover': { boxShadow: 'none' },
            }}
          >
            Create Agent
          </Button>
        </Box>
      ),
    });
    return () => setPageMeta({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    setPageMeta,
    currentApp?.client_name,
    currentApp?.url_code,
    currentApp?.client_mode,
    currentApp?.id,
  ]);

  useEffect(() => {
    if (currentApp?.id) {
      fetchAgents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentApp?.id, businessUnitId]);

  const fetchAgents = async () => {
    setLoading(true);
    try {
      const query = businessUnitId
        ? `/v1/apps/${currentApp.id}/agents?business_unit_id=${businessUnitId}`
        : `/v1/apps/${currentApp.id}/agents`;
      const response = await makeRequest(query, { method: 'GET', authTokens: tokens });
      setAgents(Array.isArray(response) ? response : response?.agents || []);
    } catch {
      // silently handled
    } finally {
      setLoading(false);
    }
  };

  const appLogoSrc = currentApp?.logo_url || FILES_CLOUDFRONT_BASE_URL + PLACEHOLDER_LOGO_URL_S3;

  const exportConfirmDialog = (
    <Dialog
      open={exportConfirmOpen}
      onClose={() => setExportConfirmOpen(false)}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}
      >
        <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: SLATE[900] }}>
          Export Client
        </Typography>
        <IconButton
          size="small"
          onClick={() => setExportConfirmOpen(false)}
          sx={{ color: SLATE[400] }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: '8px !important' }}>
        <Typography sx={{ fontSize: '0.875rem', color: SLATE[600], mb: 3 }}>
          {`Export all data for ${currentApp?.client_name || 'this client'}? This may take a moment.`}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button
            onClick={() => setExportConfirmOpen(false)}
            variant="outlined"
            size="small"
            sx={{
              borderColor: SLATE[300],
              color: SLATE[700],
              textTransform: 'none',
              borderRadius: '8px',
              '&:hover': { borderColor: SLATE[400], bgcolor: SLATE[50] },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmExportApp}
            variant="contained"
            size="small"
            sx={{
              bgcolor: INDIGO[600],
              textTransform: 'none',
              borderRadius: '8px',
              boxShadow: 'none',
              '&:hover': { bgcolor: INDIGO[700], boxShadow: 'none' },
            }}
          >
            Export
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );

  if (loading) {
    return (
      <>
        <LoadingSpinner
          message="Loading agents..."
          subMessage={`Fetching agents for ${currentApp?.client_name || 'this client'}`}
        />
        {exportConfirmDialog}
      </>
    );
  }

  return (
    <>
      {agents.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 10, width: '100%' }}>
          <Typography sx={{ fontSize: '2rem', mb: 1 }}>🤖</Typography>
          <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: SLATE[900], mb: '6px' }}>
            No agents yet
          </Typography>
          <Typography sx={{ fontSize: '0.875rem', color: SLATE[500] }}>
            Create your first agent to get started
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            width: '100%',
            display: 'grid',
            gridTemplateColumns: 'repeat(5, 1fr)',
            gap: '16px',
            '@media (max-width: 1400px)': { gridTemplateColumns: 'repeat(4, 1fr)' },
            '@media (max-width: 1100px)': { gridTemplateColumns: 'repeat(3, 1fr)' },
            '@media (max-width: 768px)': { gridTemplateColumns: 'repeat(2, 1fr)' },
            '@media (max-width: 480px)': { gridTemplateColumns: 'repeat(1, 1fr)' },
          }}
        >
          {agents.map(agent => {
            const agentLogo = agent.logo || agent.metadata?.logo_url || appLogoSrc;
            const isLive = !agent.status || agent.status === 'active';
            const viewUrl =
              agent.metadata?.has_embedded_app && agent.metadata?.agent_service_name
                ? `/${currentApp.url_code}/agent/${agent.id}/view`
                : null;

            return (
              <AgentCard
                key={agent.id}
                logoSrc={agentLogo}
                name={agent.name}
                isLive={isLive}
                agentMode={agent.agent_mode}
                onView={viewUrl ? () => window.open(viewUrl, '_blank') : undefined}
                onBuild={
                  agent.agent_mode === 'agent'
                    ? () => {
                        if (agent.site_code) dispatch(setSelectedAgentSiteCode(agent.site_code));
                      dispatch(setSelectedAgentName(agent.name));
                      dispatch(setSelectedAgentMode(agent.agent_mode || null));
                      navigate(`/${currentApp.url_code}/agent/${agent.id}/chat`);
                    }
                    : undefined
                }
                onConfigure={() => {
                  if (agent.site_code) dispatch(setSelectedAgentSiteCode(agent.site_code));
                  dispatch(setSelectedAgentName(agent.name));
                  dispatch(setSelectedAgentMode(agent.agent_mode || null));
                  navigate(`/${currentApp.url_code}/agent/${agent.id}/profile`);
                }}
              />
            );
          })}
        </Box>
      )}
      {exportConfirmDialog}
    </>
  );
}
